import { useCallback, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import { millFormOsornoBackofficeSchema } from "../../../../../validations/schemas";
import { useTranslation } from "react-i18next";
import {
    TextField,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Button,
    Grid,
    Checkbox,
    FormHelperText,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Typography,
    Divider,
} from "@mui/material";
import { useStyles } from "./MillForm.styles";
import { USA_COUNTRY_ID, USERTYPE, NoDivisionEnum } from "../../../../../constants/constants";
import { IMillForm, IMillFormValues } from "./MillFormInterfaces";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { emailRegEx, onlyNumbersRegex } from "validations/matchers";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { IDivision, IRegion } from "../CorporationForm/CorporationFormInterfaces";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import GooglePlacesAddressFields from "components/GooglePlacesAddressFields";

export const MillForm = ({ corporation, closeForm, onCancel, hideSameAsCorporationAddress, millData }: IMillForm) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { apolloMutationError, upsertMillWithManager } = MillsEntity.UpsertMillWithManagerOsornoBackoffice();
    const { corporationAdmins } = CorporationsEntity.GetCorporationAdmins(
        corporation!.corporate_id,
        USERTYPE.ADMINISTRATOR,
    );

    const [showEmailBelongsToCorporateAdmin, setShowEmailBelongsToCorporateAdmin] = useState<boolean>(false);
    const [showEmailExistOnEditNewUserList, setShowEmailExistOnEditNewUserList] = useState<boolean>(false);
    const [showNoRegionWarning, setShowRegionWarning] = useState<boolean>(false);
    const [editUsersList, setEditUsersList] = useState<
        | {
              user_id?: number | undefined;
              first_name: string;
              last_name: string;
              email?: string | undefined;
              corporate_id?: number | undefined;
              user_type_id?: number | undefined;
              mill_id?: number | undefined;
          }[]
    >([]);

    const divisions: IDivision[] = useMemo(() => corporation?.divisions || [], [corporation?.divisions]);

    const noDivision = useMemo(
        () => ({
            division_id: NoDivisionEnum.NO_DIVISION,
            name: t("setup_corporation.mill_form.no_division"),
            regions: [],
        }),
        [t],
    );

    const selectedRegionBasedOnDivision = useMemo(
        () =>
            divisions?.find((division: IDivision) => division.division_id === millData?.division?.division_id)
                ?.regions as IRegion[],
        [divisions, millData?.division?.division_id],
    );

    const isEditForm = useMemo(() => Boolean(millData && millData.mill_id), [millData]);
    const initFormValues: IMillFormValues = {
        name: millData?.name || "",
        code: millData?.code || "",
        division_id: millData?.division?.division_id || NoDivisionEnum.NO_DIVISION,
        region_id: millData?.region?.region_id || undefined,
        country_id: Number(millData?.country?.id) || USA_COUNTRY_ID,
        physical_address: millData?.physical_address || "",
        physical_address_2: millData?.physical_address_2 || "",
        physical_state_name: millData?.physical_state_name || "",
        physical_city_name: millData?.physical_city_name || "",
        physical_zip_code: millData?.physical_zip_code || "",
        physical_country_id: millData?.physical_country_id || USA_COUNTRY_ID,
        billing_address: millData?.billing_address || "",
        billing_address_2: millData?.billing_address_2 || "",
        billing_state_name: millData?.billing_state_name || "",
        billing_city_name: millData?.billing_city_name || "",
        billing_zip_code: millData?.billing_zip_code || "",
        billing_country_id: millData?.billing_country_id || USA_COUNTRY_ID,
        phone_number: millData?.phone_number || "",
        url: millData?.url || "",
        naics_code: millData?.naics_code?.toString() || "",
        dont_add_new_mill_manager: isEditForm ? true : false,
        first_name: "",
        last_name: "",
        email: "",
        regions: selectedRegionBasedOnDivision || [],
    };

    const handleSubmit = useCallback(
        async (values: IMillFormValues) => {
            try {
                const userIsCorporateAdmin = corporationAdmins?.find((user) => user.email === values.email);

                if (userIsCorporateAdmin) {
                    setShowEmailBelongsToCorporateAdmin(true);
                } else {
                    setShowEmailBelongsToCorporateAdmin(false);
                }

                const response = await upsertMillWithManager({
                    variables: {
                        mill: {
                            ...(isEditForm && {
                                mill_id: millData?.mill_id,
                            }),
                            corporate_id: corporation && corporation?.corporate_id,
                            name: values.name,
                            ...(values.code && { code: values.code }),
                            naics_code: values.naics_code,
                            code: values.code,
                            division_id: values.division_id !== NoDivisionEnum.NO_DIVISION ? values.division_id : null,
                            region_id: values.division_id !== NoDivisionEnum.NO_DIVISION ? values.region_id : null,
                            country_id: values.country_id,
                            url: values.url,
                            physical_address: values.physical_address,
                            physical_address_2: values.physical_address_2,
                            physical_state_name: values.physical_state_name,
                            physical_city_name: values.physical_city_name,
                            physical_zip_code: values.physical_zip_code,
                            physical_country_id: values.physical_country_id,
                            billing_address: values.billing_address,
                            billing_address_2: values.billing_address_2,
                            billing_state_name: values.billing_state_name,
                            billing_city_name: values.billing_city_name,
                            billing_zip_code: values.billing_zip_code,
                            billing_country_id: values.billing_country_id,
                            phone_number: values.phone_number,
                            ...(!isEditForm && {
                                users: {
                                    data: values.dont_add_new_mill_manager
                                        ? []
                                        : [
                                              {
                                                  first_name: values.first_name,
                                                  last_name: values.last_name,
                                                  email: values.email,
                                                  corporate_id: corporation && corporation?.corporate_id,
                                                  user_type_id: USERTYPE.MANAGER,
                                              },
                                          ],
                                },
                            }),
                        },
                        user: !isEditForm ? [] : editUsersList,
                    },
                });
                if (response) {
                    closeForm();
                }
            } catch {
                return;
            }
        },
        [
            closeForm,
            corporation,
            corporationAdmins,
            editUsersList,
            isEditForm,
            millData?.mill_id,
            upsertMillWithManager,
        ],
    );

    const currentEditUsersList = useMemo(
        () => [
            ...(millData?.users ? millData.users.filter((user) => user.user_type_id !== USERTYPE.ADMINISTRATOR) : []),
            ...(editUsersList ? editUsersList : []),
        ],
        [editUsersList, millData?.users],
    );

    return (
        <div>
            <div className={classes.titleContainer}>
                <h1 className={classes.formTitle}>
                    {isEditForm
                        ? t("setup_corporation.mill_form.title_edit")
                        : t("setup_corporation.mill_form.title_add")}
                </h1>
            </div>
            <Formik
                initialValues={initFormValues}
                validationSchema={millFormOsornoBackofficeSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, values, handleChange, setFieldValue, validateForm }) => {
                    return (
                        <Form className={classes.fieldsContainer}>
                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>
                                    {t("setup_corporation.mill_form.subtitle_mill")}
                                </h3>
                            </div>
                            <TextField
                                fullWidth
                                id="outlined-name-input"
                                name="name"
                                type="text"
                                variant="outlined"
                                autoFocus={true}
                                className={classes.textField}
                                label={t("setup_corporation.mill_form.name")}
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                            <TextField
                                fullWidth
                                id="outlined-code-input"
                                name="code"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.mill_form.code")}
                                value={values.code}
                                onChange={handleChange}
                            />
                            {divisions.length > 0 ? (
                                <>
                                    <FormControl
                                        error={touched.division_id && Boolean(errors.division_id)}
                                        className={classes.textField}
                                        fullWidth
                                    >
                                        <InputLabel id="demo-simple-select-label">
                                            {t("setup_corporation.mill_form.division")}
                                        </InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            className={classes.select}
                                            name="division_id"
                                            value={values.division_id || ""}
                                            label={t("setup_corporation.mill_form.division")}
                                            onChange={handleChange}
                                        >
                                            {[noDivision, ...divisions].map((division, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={division.division_id}
                                                    onClick={() => {
                                                        setFieldValue("regions", division.regions);
                                                        if (division.regions.length < 1) {
                                                            setShowRegionWarning(true);
                                                        } else {
                                                            setShowRegionWarning(false);
                                                        }
                                                    }}
                                                >
                                                    {division.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {touched.division_id && errors.division_id && t(errors.division_id)}
                                        </FormHelperText>
                                    </FormControl>
                                    {values.division_id !== NoDivisionEnum.NO_DIVISION ? (
                                        <FormControl
                                            error={touched.region_id && Boolean(errors.region_id)}
                                            className={classes.textField}
                                            fullWidth
                                        >
                                            <InputLabel id="demo-simple-select-label">
                                                {t("setup_corporation.mill_form.region")}
                                            </InputLabel>
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                className={classes.select}
                                                name="region_id"
                                                value={values.region_id || ""}
                                                label={t("setup_corporation.mill_form.region")}
                                                onChange={handleChange}
                                            >
                                                {values.regions.map((region, index) => (
                                                    <MenuItem key={index} value={region.region_id}>
                                                        {region.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.region_id && errors.region_id && t(errors.region_id)}
                                            </FormHelperText>
                                            {showNoRegionWarning && (
                                                <FormHelperText className={classes.error}>
                                                    {t("setup_corporation.mill_form.no_region_warning")}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    ) : null}
                                </>
                            ) : null}

                            <Divider textAlign="left" className={classes.divider}>
                                {t("setup_corporation.mill_form.physical_address")}
                            </Divider>

                            <GooglePlacesAddressFields
                                address1FieldName="physical_address"
                                address2FieldName="physical_address_2"
                                cityNameFieldName="physical_city_name"
                                stateNameFieldName="physical_state_name"
                                zipCodeFieldName="physical_zip_code"
                                countryIdFieldName="physical_country_id"
                                initialValues={initFormValues}
                            />

                            <div className={classes.billingAddressContainer}>
                                <Divider textAlign="left" className={classes.divider}>
                                    {t("setup_corporation.mill_form.billing_address")}
                                </Divider>
                                <FormControlLabel
                                    className={
                                        Boolean(touched.billing_address && errors.billing_address)
                                            ? classes.checkboxLabelErrors
                                            : classes.checkboxLabel
                                    }
                                    control={
                                        <Checkbox
                                            className={classes.checkbox}
                                            onChange={(event) => {
                                                if (event.target.checked) {
                                                    setFieldValue("billing_address", values.physical_address);
                                                    setFieldValue("billing_address_2", values.physical_address_2);
                                                    setFieldValue("billing_city_name", values.physical_city_name);
                                                    setFieldValue("billing_state_name", values.physical_state_name);
                                                    setFieldValue("billing_zip_code", values.physical_zip_code);
                                                    setFieldValue("billing_country_id", values.physical_country_id);
                                                } else {
                                                    setFieldValue("billing_address", millData?.billing_address || "");
                                                    setFieldValue(
                                                        "billing_address_2",
                                                        millData?.billing_address_2 || "",
                                                    );
                                                    setFieldValue(
                                                        "billing_city_name",
                                                        millData?.billing_city_name || "",
                                                    );
                                                    setFieldValue(
                                                        "billing_state_name",
                                                        millData?.billing_state_name || "",
                                                    );
                                                    setFieldValue("billing_zip_code", millData?.billing_zip_code || "");
                                                    setFieldValue(
                                                        "billing_country_id",
                                                        millData?.billing_country_id || USA_COUNTRY_ID,
                                                    );
                                                }
                                            }}
                                        />
                                    }
                                    label={t("setup_corporation.mill_form.billing_address_checkbox_label")}
                                />
                                <div className={classes.billingAddressContainerFields}>
                                    <GooglePlacesAddressFields
                                        address1FieldName="billing_address"
                                        address2FieldName="billing_address_2"
                                        cityNameFieldName="billing_city_name"
                                        stateNameFieldName="billing_state_name"
                                        zipCodeFieldName="billing_zip_code"
                                        countryIdFieldName="billing_country_id"
                                        initialValues={initFormValues}
                                    />
                                </div>
                            </div>

                            <TextField
                                fullWidth
                                id="outlined-url-input"
                                name="url"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.mill_form.url")}
                                value={values.url}
                                onChange={handleChange}
                                error={touched.url && Boolean(errors.url)}
                                helperText={touched.url && errors.url && t(errors.url)}
                            />
                            <TextField
                                fullWidth
                                id="outlined-phone_number-input"
                                name="phone_number"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.mill_form.phone_number")}
                                value={values.phone_number}
                                onChange={handleChange}
                                error={touched.phone_number && Boolean(errors.phone_number)}
                                helperText={touched.phone_number && errors.phone_number && t(errors.phone_number)}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="outlined-naics_code-input"
                                        name="naics_code"
                                        type="text"
                                        variant="outlined"
                                        className={classes.textField}
                                        label={t("setup_corporation.mill_form.naics_code")}
                                        value={values.naics_code}
                                        onChange={(e) => {
                                            if (e.target.value === "" || onlyNumbersRegex.test(e.target.value)) {
                                                handleChange(e);
                                            }
                                        }}
                                        error={touched.naics_code && Boolean(errors.naics_code)}
                                        helperText={touched.naics_code && errors.naics_code && t(errors.naics_code)}
                                    />
                                    <div className={classes.naicsCodeLink}>
                                        <InfoIcon color="info" />
                                        {t("setup_corporation.mill_form.you_can_find_the_naics_code")}
                                        <a
                                            href="https://www.naics.com/code-search/?trms=lumber&v=2022&styp=naics&qid=2c790097a4"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t("setup_corporation.mill_form.here")}
                                        </a>
                                    </div>
                                </Grid>
                            </Grid>

                            <div className={classes.separator} />

                            {isEditForm && (
                                <>
                                    <div className={classes.titleContainer}>
                                        <h3 className={classes.formSubtitle}>
                                            {t("setup_corporation.mill_form.mill_managers")}
                                        </h3>
                                    </div>
                                    {!currentEditUsersList.length && (
                                        <div className={classes.noMillManagersWrapper}>
                                            <InfoIcon color="warning" />
                                            {t("setup_corporation.mill_form.no_mill_managers")}
                                        </div>
                                    )}

                                    {!!currentEditUsersList.length && (
                                        <List className={classes.listContainer}>
                                            {currentEditUsersList.map((user, index) => {
                                                return (
                                                    <div key={index} className={classes.separatorNoPadding}>
                                                        <ListItem key={index} className={classes.listItem}>
                                                            <ListItemText
                                                                className={classes.listText}
                                                                primary={
                                                                    <div className={classes.listItemContainer}>
                                                                        {!user.user_id && (
                                                                            <Tooltip
                                                                                PopperProps={{
                                                                                    className: classes.tooltip,
                                                                                }}
                                                                                title={
                                                                                    <Typography
                                                                                        className={classes.tooltipTexts}
                                                                                    >
                                                                                        {t(
                                                                                            "setup_corporation.mill_form.mill_user_add_saved_permamently",
                                                                                        )}
                                                                                    </Typography>
                                                                                }
                                                                            >
                                                                                <InfoIcon color="warning" />
                                                                            </Tooltip>
                                                                        )}
                                                                        <div
                                                                            className={classes.listContainerTitle}
                                                                        >{`${user.first_name} ${user.last_name}`}</div>
                                                                        <div className={classes.listContainerEmail}>
                                                                            ({user.email})
                                                                        </div>
                                                                    </div>
                                                                }
                                                            />
                                                            {!user.user_id && (
                                                                <IconButton
                                                                    onClick={() => {
                                                                        setEditUsersList(
                                                                            editUsersList?.filter(
                                                                                (editUser) =>
                                                                                    editUser.email !== user.email,
                                                                            ),
                                                                        );
                                                                    }}
                                                                    disabled={!!user.user_id}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            )}
                                                        </ListItem>
                                                    </div>
                                                );
                                            })}
                                        </List>
                                    )}

                                    <>
                                        <div className={classes.titleContainer}>
                                            {t("setup_corporation.mill_form.add_mill_manager")}
                                        </div>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-first_name-input"
                                                    name="first_name"
                                                    type="text"
                                                    variant="outlined"
                                                    className={classes.textField}
                                                    label={t("setup_corporation.mill_form.first_name")}
                                                    value={values.first_name}
                                                    onChange={handleChange}
                                                    error={touched.first_name && Boolean(errors.first_name)}
                                                    helperText={
                                                        touched.first_name && errors.first_name && t(errors.first_name)
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-last_name-input"
                                                    name="last_name"
                                                    type="text"
                                                    variant="outlined"
                                                    className={classes.textField}
                                                    label={t("setup_corporation.mill_form.last_name")}
                                                    value={values.last_name}
                                                    onChange={handleChange}
                                                    error={touched.last_name && Boolean(errors.last_name)}
                                                    helperText={
                                                        touched.last_name && errors.last_name && t(errors.last_name)
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        <TextField
                                            fullWidth
                                            id="outlined-email-input"
                                            name="email"
                                            type="text"
                                            variant="outlined"
                                            className={classes.textField}
                                            label={t("setup_corporation.mill_form.email")}
                                            value={values.email}
                                            onChange={handleChange}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email && t(errors.email)}
                                        />

                                        <p style={{ display: apolloMutationError ? "block" : "none", color: "red" }}>
                                            {apolloMutationError?.message.includes("uq_mill_name")
                                                ? t("setup_corporation.errors.existing_mill_name")
                                                : showEmailBelongsToCorporateAdmin
                                                ? t("setup_corporation.errors.existing_administrator")
                                                : apolloMutationError?.message.includes("uq_user_email")
                                                ? t("setup_corporation.errors.existing_email")
                                                : t("setup_corporation.errors.creation_error")}
                                        </p>
                                        <p
                                            style={{
                                                display: showEmailExistOnEditNewUserList ? "block" : "none",
                                                color: "red",
                                            }}
                                        >
                                            {!!showEmailExistOnEditNewUserList && (
                                                <div className={classes.noMillManagersWrapper}>
                                                    <InfoIcon color="warning" />
                                                    {t("setup_corporation.errors.email_exists_on_list")}
                                                </div>
                                            )}
                                        </p>
                                        <div className={classes.addMillManagerActions}>
                                            <Button
                                                className={classes.submitButton}
                                                variant="contained"
                                                disabled={
                                                    isSubmitting ||
                                                    !values.first_name ||
                                                    !values.last_name ||
                                                    !values.email ||
                                                    emailRegEx.test(values.email) === false
                                                }
                                                onClick={() => {
                                                    const emailExists = currentEditUsersList.find(
                                                        (user) => user.email === values.email,
                                                    );

                                                    if (emailExists) {
                                                        setShowEmailExistOnEditNewUserList(true);
                                                    } else {
                                                        setShowEmailExistOnEditNewUserList(false);
                                                    }

                                                    if (
                                                        values.first_name &&
                                                        values.last_name &&
                                                        values.email &&
                                                        !showEmailBelongsToCorporateAdmin &&
                                                        !emailExists
                                                    ) {
                                                        setEditUsersList([
                                                            ...(editUsersList ? editUsersList : []),
                                                            {
                                                                first_name: values.first_name,
                                                                last_name: values.last_name,
                                                                email: values.email,
                                                                corporate_id: corporation && corporation?.corporate_id,
                                                                user_type_id: USERTYPE.MANAGER,
                                                                mill_id: millData?.mill_id,
                                                            },
                                                        ]);
                                                        setFieldValue("first_name", "");
                                                        setFieldValue("last_name", "");
                                                        setFieldValue("email", "");
                                                        validateForm();
                                                    }
                                                }}
                                            >
                                                {t("setup_corporation.mill_form.add_manager_button")}
                                            </Button>
                                        </div>
                                    </>
                                    <div className={classes.separator} />
                                </>
                            )}

                            {!isEditForm && (
                                <>
                                    <div className={classes.titleContainer}>
                                        <h3 className={classes.formSubtitle}>
                                            {t("setup_corporation.mill_form.subtitle_user")}
                                        </h3>
                                    </div>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} className={classes.useCorporateAdminCheckbox}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        className={classes.checkbox}
                                                        disabled={false}
                                                        checked={values.dont_add_new_mill_manager}
                                                        onChange={(event) => {
                                                            if (event.target.checked) {
                                                                setFieldValue("dont_add_new_mill_manager", true);
                                                                setFieldValue("first_name", "");
                                                                setFieldValue("last_name", "");
                                                                setFieldValue("email", "");
                                                                validateForm();
                                                            } else {
                                                                setFieldValue("dont_add_new_mill_manager", false);
                                                                validateForm();
                                                            }
                                                        }}
                                                        inputProps={{ "aria-label": "controlled" }}
                                                        value={values.dont_add_new_mill_manager}
                                                        id={"dont_add_new_mill_manager"}
                                                    />
                                                }
                                                label={t("setup_corporation.mill_form.dont_add_new_mill_manager")}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                disabled={values.dont_add_new_mill_manager}
                                                id="outlined-first_name-input"
                                                name="first_name"
                                                type="text"
                                                variant="outlined"
                                                className={classes.textField}
                                                label={t("setup_corporation.mill_form.first_name")}
                                                value={values.first_name}
                                                onChange={handleChange}
                                                error={
                                                    !values.dont_add_new_mill_manager &&
                                                    touched.first_name &&
                                                    Boolean(errors.first_name)
                                                }
                                                helperText={
                                                    !values.dont_add_new_mill_manager &&
                                                    touched.first_name &&
                                                    errors.first_name &&
                                                    t(errors.first_name)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                disabled={values.dont_add_new_mill_manager}
                                                id="outlined-last_name-input"
                                                name="last_name"
                                                type="text"
                                                variant="outlined"
                                                className={classes.textField}
                                                label={t("setup_corporation.mill_form.last_name")}
                                                value={values.last_name}
                                                onChange={handleChange}
                                                error={
                                                    !values.dont_add_new_mill_manager &&
                                                    touched.last_name &&
                                                    Boolean(errors.last_name)
                                                }
                                                helperText={
                                                    !values.dont_add_new_mill_manager &&
                                                    touched.last_name &&
                                                    errors.last_name &&
                                                    t(errors.last_name)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                    <TextField
                                        fullWidth
                                        disabled={values.dont_add_new_mill_manager}
                                        id="outlined-email-input"
                                        name="email"
                                        type="text"
                                        variant="outlined"
                                        className={classes.textField}
                                        label={t("setup_corporation.mill_form.email")}
                                        value={values.email}
                                        onChange={handleChange}
                                        error={
                                            !values.dont_add_new_mill_manager && touched.email && Boolean(errors.email)
                                        }
                                        helperText={
                                            !values.dont_add_new_mill_manager &&
                                            touched.email &&
                                            errors.email &&
                                            t(errors.email)
                                        }
                                    />

                                    <p style={{ display: apolloMutationError ? "block" : "none", color: "red" }}>
                                        {apolloMutationError?.message.includes("uq_mill_name")
                                            ? t("setup_corporation.errors.existing_mill_name")
                                            : showEmailBelongsToCorporateAdmin
                                            ? t("setup_corporation.errors.existing_administrator")
                                            : apolloMutationError?.message.includes("uq_user_email")
                                            ? t("setup_corporation.errors.existing_email")
                                            : t("setup_corporation.errors.creation_error")}
                                    </p>
                                </>
                            )}

                            <div className={classes.titleContainer}>
                                {!!onCancel && (
                                    <Button
                                        className={classes.cancelButton}
                                        variant="contained"
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={onCancel}
                                    >
                                        {t("setup_corporation.mill_form.cancel_button")}
                                    </Button>
                                )}
                                <Button
                                    className={classes.submitButton}
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {t("setup_corporation.mill_form.submit_button")}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};
